import { useCallback, useMemo } from 'react'

import { FormGroup, Input, Label, Select, SelectMulti, ToggleOptionGroup, ToggleOptionGroupItem } from 'twyne-new.ui'

import {
  AUTO_INTERVAL,
  CHART_INTERVALS,
  acceptsIntervalQuantity,
  resolveChartInterval,
  resolveChartIntervalQuantity,
  supportsInterval,
} from '../utils/chartIntervals'
import { collapsesChartIndex, getGroupbyLabel, isAxisFieldAllowed } from '../utils/chartUtils'
import { getMetricDisplayLabel } from '../utils/collectionMetrics'
import {
  CHART_SIZE_OPTIONS,
  CHART_TYPES,
  applyChartAxis,
  applyChartType,
  getChartDimensionOptions,
  getChartMetricOptions,
  getDefaultChartName,
} from '../utils/constants'

import IntervalQuantityInput from './IntervalQuantityInput'

const SERIES_BY_METRICS = { value: null, label: 'Metrics' }

const GROUPING_LABELS = {
  'pie-chart': { field: 'Slices by', hint: 'Add at least two metrics to compare slices.' },
  'funnel-chart': { field: 'Steps by', hint: 'Add at least two metrics to build the funnel steps.' },
}

function toMultiValue(values = [], options = []) {
  return values.map(value => options.find(option => option.value === value) || { value, label: value })
}

function toGroupbyOptions(collection, fields = []) {
  return fields.map(field => ({ value: field, label: getGroupbyLabel(collection, field) }))
}

function ChartSizeToggle({ value = 'full', onChange }) {
  return (
    <ToggleOptionGroup
      type="single"
      className="w-fit"
      value={value}
      onValueChange={next => next && onChange(next)}
    >
      {CHART_SIZE_OPTIONS.map(option => <ToggleOptionGroupItem
        key={option.value}
        value={option.value}
        className="px-3 py-1.5 text-xs leading-none min-h-0 h-7"
      >
        {option.label}
      </ToggleOptionGroupItem>)}
    </ToggleOptionGroup>
  )
}

export function isChartFormValid(form) {
  if (!form?.metricKeys?.length) return false
  if (collapsesChartIndex(form?.dataType)) return true

  const [dimension] = form?.dimensions || []
  return isAxisFieldAllowed(dimension, form?.dataType)
}

export default function ChartForm({ config, form, setForm }) {
  const collapsesIndex = collapsesChartIndex(form.dataType)
  const grouping = GROUPING_LABELS[form.dataType]

  const metricOptions = useMemo(
    () => getChartMetricOptions(config.collection),
    [config.collection],
  )

  const breakdownOptions = useMemo(
    () => (form.metricKeys || []).map(key => ({
      value: key,
      label: getMetricDisplayLabel(config.collection, { key }),
    })),
    [config.collection, form.metricKeys],
  )

  const xAxisOptions = useMemo(
    () => getChartDimensionOptions(config.collection, form.dataType),
    [config.collection, form.dataType],
  )

  const seriesByOptions = useMemo(() => {
    const groupby = config.groupby || []
    if (collapsesIndex) return [SERIES_BY_METRICS, ...toGroupbyOptions(config.collection, groupby)]

    const available = groupby.filter(field => !form.dimensions?.includes(field))
    return [SERIES_BY_METRICS, ...toGroupbyOptions(config.collection, available)]
  }, [collapsesIndex, config.collection, config.groupby, form.dimensions])

  const handleMetricKeysChange = useCallback(options => {
    const metricKeys = (options || []).map(option => option.value)
    setForm(prev => ({
      ...prev,
      metricKeys,
      breakdown: metricKeys.includes(prev.breakdown) ? prev.breakdown : metricKeys[0] || 'total',
    }))
  }, [setForm])

  const handleSingleMetricChange = useCallback(option => {
    const key = option?.value
    if (!key) return

    setForm(prev => ({ ...prev, metricKeys: [key], breakdown: key }))
  }, [setForm])

  const handleXAxisChange = useCallback(option => {
    setForm(prev => applyChartAxis(prev, option?.value))
  }, [setForm])

  const handleTypeChange = useCallback(option => {
    setForm(prev => applyChartType(prev, option?.value, config.groupby))
  }, [config.groupby, setForm])

  const resolvedInterval = resolveChartInterval(form, config)
  const intervalQuantity = resolveChartIntervalQuantity(form, config)
  const showQuantity = acceptsIntervalQuantity(resolvedInterval)

  const intervalHint = useMemo(() => {
    if (!resolvedInterval) return ''

    const option = CHART_INTERVALS.find(item => item.value === resolvedInterval)
    const label = (option?.label || resolvedInterval).toLowerCase()
    const width = intervalQuantity > 1 ? `${intervalQuantity} ${resolvedInterval}s` : label

    if (form.interval && form.interval !== AUTO_INTERVAL) {
      return `The chart groups the x axis by ${width} buckets.`
    }

    return `Auto resolves to ${width} buckets for the selected date range.`
  }, [form.interval, intervalQuantity, resolvedInterval])

  const singleMetric = collapsesIndex && Boolean(form.seriesBy)

  return <>
    <FormGroup>
      <Label>Name</Label>
      <Input
        value={form.name || ''}
        onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))}
        placeholder={getDefaultChartName(config, form.metricKeys)}
      />
    </FormGroup>
    <FormGroup>
      <Label>Type</Label>
      <Select
        options={CHART_TYPES}
        value={form.dataType}
        onChange={handleTypeChange}
      />
    </FormGroup>
    {!collapsesIndex && <FormGroup>
      <Label>X axis</Label>
      <Select
        options={xAxisOptions}
        value={form.dimensions?.[0] ?? null}
        onChange={handleXAxisChange}
        placeholder="Select the x axis"
      />
    </FormGroup>}
    {!collapsesIndex && supportsInterval(form.dimensions?.[0]) && <FormGroup>
      <Label>Granularity</Label>
      <div className="flex items-center gap-2">
        {showQuantity && <IntervalQuantityInput
          value={intervalQuantity}
          onChange={quantity => setForm(prev => ({ ...prev, intervalQuantity: quantity }))}
        />}
        <div className="min-w-0 flex-1">
          <Select
            options={CHART_INTERVALS}
            value={form.interval || AUTO_INTERVAL}
            onChange={option => setForm(prev => ({ ...prev, interval: option?.value || AUTO_INTERVAL }))}
          />
        </div>
      </div>
      <span className="mt-1 block text-xs twy-text-base-muted">
        {intervalHint}
      </span>
    </FormGroup>}
    <FormGroup>
      <Label>{grouping?.field || 'Series by'}</Label>
      <Select
        options={seriesByOptions}
        value={form.seriesBy}
        onChange={option => setForm(prev => ({ ...prev, seriesBy: option?.value ?? null }))}
      />
    </FormGroup>
    {singleMetric ? <FormGroup>
      <Label>Value</Label>
      <Select
        options={metricOptions}
        value={form.metricKeys?.[0] ?? null}
        onChange={handleSingleMetricChange}
        placeholder="Select the metric"
      />
    </FormGroup> : <FormGroup>
      <Label>Metric</Label>
      <SelectMulti
        options={metricOptions}
        value={toMultiValue(form.metricKeys, metricOptions)}
        onChange={handleMetricKeysChange}
        placeholder="Select metrics"
      />
      {grouping && form.metricKeys?.length < 2 && <span className="mt-1 block text-xs twy-text-base-muted">
        {grouping.hint}
      </span>}
    </FormGroup>}
    {!collapsesIndex && form.seriesBy && <FormGroup>
      <Label>Breakdown</Label>
      <Select
        options={breakdownOptions}
        value={form.breakdown}
        onChange={option => setForm(prev => ({ ...prev, breakdown: option?.value || prev.metricKeys[0] }))}
      />
    </FormGroup>}
    <FormGroup>
      <Label>Size</Label>
      <ChartSizeToggle
        value={form.layoutSize || 'full'}
        onChange={layoutSize => setForm(prev => ({ ...prev, layoutSize }))}
      />
    </FormGroup>
  </>
}
